import { useState, useEffect, useRef } from 'react'
import { Tip } from '@ofself/shell'

const CHAT_API = import.meta.env.VITE_CHAT_API_URL || ''

/**
 * Dropdown shown when the user types "/" in chat.
 * Shows two sections: Personas (headless agents) and Plugins (sub-entity plugins).
 * onSelect receives an item with a `type` field: 'persona' | 'plugin'.
 */
export default function PluginPicker({ query, onSelect, onClose, channelId }) {
  const [plugins,  setPlugins]  = useState([])
  const [personas, setPersonas] = useState([])
  const [loading,  setLoading]  = useState(true)
  const [selectedIndex, setSelectedIndex] = useState(0)
  const containerRef = useRef(null)

  useEffect(() => {
    fetchAll()
  }, [channelId]) // eslint-disable-line react-hooks/exhaustive-deps

  async function fetchAll() {
    setLoading(true)
    const token  = localStorage.getItem('chat_session_token')
    const params = channelId ? `?channel_id=${channelId}` : ''
    const headers = { Authorization: `Bearer ${token}` }

    const [pluginRes, personaRes] = await Promise.allSettled([
      fetch(`${CHAT_API}/api/chat/plugins/discover${params}`, { headers }).then(r => r.ok ? r.json() : { plugins: [] }),
      fetch(`${CHAT_API}/api/chat/personas/discover${params}`, { headers }).then(r => r.ok ? r.json() : { personas: [] }),
    ])

    setPlugins(pluginRes.status  === 'fulfilled' ? (pluginRes.value.plugins   || []) : [])
    setPersonas(personaRes.status === 'fulfilled' ? (personaRes.value.personas || []) : [])
    setLoading(false)
  }

  // Persona names/slugs — used to deduplicate plugins that are already exposed as personas
  const personaNameSet = new Set(personas.map(p => p.name?.toLowerCase()).filter(Boolean))
  const personaSlugSet = new Set(personas.map(p => p.slug?.toLowerCase()).filter(Boolean))

  // Flatten to a single ordered list: personas first, then plugins (excluding duplicates)
  const filtered = [
    ...personas
      .filter(p => {
        if (!query) return true
        const q = query.toLowerCase()
        return p.name?.toLowerCase().includes(q) || p.slug?.toLowerCase().includes(q)
      })
      .map(p => ({ ...p, type: 'persona', key: `persona:${p.id}` })),
    ...plugins
      .filter(p => {
        // Hide plugin if a persona with the same name or slug already covers it
        const pluginName = p.name?.toLowerCase()
        const pluginKey  = p.sub_entity_key?.toLowerCase()
        if (personaNameSet.has(pluginName) || personaSlugSet.has(pluginKey) || personaSlugSet.has(pluginName)) return false
        if (!query) return true
        const q = query.toLowerCase()
        return pluginKey?.includes(q) || pluginName?.includes(q)
      })
      .map(p => ({ ...p, type: 'plugin', key: `plugin:${p.plugin_id}` })),
  ]

  useEffect(() => {
    setSelectedIndex(0)
  }, [query])

  function handleKeyDown(e) {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setSelectedIndex(i => Math.min(i + 1, filtered.length - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setSelectedIndex(i => Math.max(i - 1, 0))
    } else if (e.key === 'Enter' || e.key === 'Tab') {
      e.preventDefault()
      if (filtered[selectedIndex]) onSelect(filtered[selectedIndex])
    } else if (e.key === 'Escape') {
      onClose()
    }
  }

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }) // intentionally no deps

  if (loading) {
    return (
      <div ref={containerRef} className="plugin-picker" style={pickerStyle}>
        <div style={{ padding: '8px 12px', fontSize: 'var(--ofself-text-base)', color: '#888' }}>Loading…</div>
      </div>
    )
  }

  if (filtered.length === 0) {
    return (
      <div ref={containerRef} className="plugin-picker" style={pickerStyle}>
        <div style={{ padding: '8px 12px', fontSize: 'var(--ofself-text-base)', color: '#888' }}>
          {personas.length === 0 && plugins.length === 0 ? 'No personas or plugins available' : 'No matches'}
        </div>
      </div>
    )
  }

  // Split filtered back into sections for labelled rendering
  const filteredPersonas = filtered.filter(x => x.type === 'persona')
  const filteredPlugins  = filtered.filter(x => x.type === 'plugin')

  let globalIdx = 0

  return (
    <div ref={containerRef} className="plugin-picker" style={pickerStyle}>

      {filteredPersonas.length > 0 && (
        <>
          <div style={sectionLabel}>
            <svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor" style={{ marginRight: 4, verticalAlign: 'middle' }}>
              <path d="M12 2a2 2 0 0 1 2 2c0 .74-.4 1.39-1 1.73V7h1a7 7 0 0 1 7 7h1a1 1 0 0 1 1 1v3a1 1 0 0 1-1 1h-1.07A7.001 7.001 0 0 1 14 23h-4a7.001 7.001 0 0 1-6.93-4H2a1 1 0 0 1-1-1v-3a1 1 0 0 1 1-1h1a7 7 0 0 1 7-7h1V5.73c-.6-.34-1-.99-1-1.73a2 2 0 0 1 2-2zm-4 13a2 2 0 1 0 0 4 2 2 0 0 0 0-4zm8 0a2 2 0 1 0 0 4 2 2 0 0 0 0-4z"/>
            </svg>
            Personas
          </div>
          {filteredPersonas.map(item => {
            const idx = globalIdx++
            return (
              <div
                key={item.key}
                onClick={() => onSelect(item)}
                style={{
                  ...itemStyle,
                  background: idx === selectedIndex ? 'var(--hover-bg, rgba(0,0,0,0.06))' : 'transparent',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ fontWeight: 'var(--ofself-weight-semibold)', fontSize: 'var(--ofself-text-base)' }}>/{item.slug || item.name?.toLowerCase().replace(/\s+/g, '-')}</span>
                  <span style={{
                    ...badgeStyle,
                    background: item.scope === 'group' ? '#2d6a4f' : 'var(--accent)',
                  }}>
                    {item.scope === 'group' ? 'Group' : 'Personal'}
                  </span>
                  {!item.has_realm && (
                    <Tip label="Needs realm assignment before first use">
                      <span style={{ ...badgeStyle, background: '#7a5520' }}>
                        setup needed
                      </span>
                    </Tip>
                  )}
                </div>
                <div style={{ fontSize: 'var(--ofself-text-sm)', color: '#888' }}>{item.name}{item.description ? ` — ${item.description.slice(0, 60)}` : ''}</div>
              </div>
            )
          })}
        </>
      )}

      {filteredPlugins.length > 0 && (
        <>
          <div style={sectionLabel}>
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginRight: 4, verticalAlign: 'middle' }}>
              <path d="M20.24 12.24a6 6 0 0 0-8.49-8.49L5 10.5V19h8.5z"/><line x1="16" y1="8" x2="2" y2="22"/><line x1="17.5" y1="15" x2="9" y2="15"/>
            </svg>
            Plugins
          </div>
          {filteredPlugins.map(item => {
            const idx = globalIdx++
            return (
              <div
                key={item.key}
                onClick={() => onSelect(item)}
                style={{
                  ...itemStyle,
                  background: idx === selectedIndex ? 'var(--hover-bg, rgba(0,0,0,0.06))' : 'transparent',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ fontWeight: 'var(--ofself-weight-semibold)', fontSize: 'var(--ofself-text-base)' }}>/{item.sub_entity_key}</span>
                  <span style={{
                    ...badgeStyle,
                    background: item.owner_type === 'group' ? '#2d6a4f' : 'var(--accent, #007bff)',
                  }}>
                    {item.owner_type === 'group' ? 'Group' : 'Personal'}
                  </span>
                </div>
                <div style={{ fontSize: 'var(--ofself-text-sm)', color: '#888' }}>
                  {item.name}{item.parent_app_name ? ` via ${item.parent_app_name}` : ''}
                </div>
              </div>
            )
          })}
        </>
      )}
    </div>
  )
}

const pickerStyle = {
  position: 'absolute',
  bottom: '100%',
  left: 0,
  right: 0,
  maxHeight: 300,
  overflowY: 'auto',
  background: 'var(--bg-primary, #fff)',
  border: '1px solid var(--border, #ddd)',
  borderRadius: 8,
  zIndex: 100,
  marginBottom: 4,
}

const sectionLabel = {
  padding: '6px 12px 3px',
  fontSize: 'var(--ofself-text-sm)',
  color: '#888',
  textTransform: 'uppercase',
  display: 'flex',
  alignItems: 'center',
  borderTop: '1px solid var(--border, #eee)',
  marginTop: 2,
}

const itemStyle = {
  padding: '6px 12px',
  cursor: 'pointer',
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
}

const badgeStyle = {
  fontSize: 'var(--ofself-text-sm)',
  padding: '1px 4px',
  borderRadius: 3,
  color: '#fff',
}
